import React, { useState } from 'react';
import { SavedQuote } from '../App';
import { PlusCircleIcon, FileTextIcon, EyeIcon, EditIcon, TrashIcon, SearchIcon, CalendarIcon, DollarSignIcon, BuildingIcon, UserIcon, CheckCircleIcon, ShoppingCartIcon, XIcon, PackageIcon } from 'lucide-react';
interface QuotesListProps {
  quotes: SavedQuote[];
  onNewQuote: () => void;
  onEditQuote: (quoteId: string) => void;
  onViewQuote: (quoteId: string) => void;
  onDeleteQuote: (quoteId: string) => void;
}
type ViewMode = 'dealer' | 'customer';
export function QuotesList({
  quotes,
  onNewQuote,
  onEditQuote,
  onViewQuote,
  onDeleteQuote
}: QuotesListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'finalized'>('all');
  const [viewModes, setViewModes] = useState<Map<string, ViewMode>>(new Map());
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedQuoteForConversion, setSelectedQuoteForConversion] = useState<SavedQuote | null>(null);
  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || quote.status === filterStatus;
    return matchesSearch && matchesStatus;
  });
  const getViewMode = (quoteId: string): ViewMode => {
    return viewModes.get(quoteId) || 'dealer';
  };
  const toggleViewMode = (quoteId: string) => {
    const newViewModes = new Map(viewModes);
    const currentMode = getViewMode(quoteId);
    newViewModes.set(quoteId, currentMode === 'dealer' ? 'customer' : 'dealer');
    setViewModes(newViewModes);
  };
  const calculatePricing = (quote: SavedQuote, viewMode: ViewMode) => {
    const partsTotal = quote.parts.reduce((sum, part) => sum + part.quantity * part.unitPrice, 0);
    if (quote.quoteType === 'dealer-customer') {
      const dealerDiscount = 10; // 10%
      const dealerMargin = 15; // 15%
      const dealerPrice = partsTotal * (1 - dealerDiscount / 100);
      const customerPrice = dealerPrice * (1 + dealerMargin / 100);
      const basePrice = viewMode === 'customer' ? customerPrice : dealerPrice;
      return basePrice + quote.transport + quote.insurance;
    }
    return partsTotal + quote.transport + quote.insurance;
  };
  const handleConvertToOrder = (quote: SavedQuote) => {
    setSelectedQuoteForConversion(quote);
    setShowConvertModal(true);
  };
  const handleConfirmConvert = () => {
    if (selectedQuoteForConversion) {
      const viewMode = getViewMode(selectedQuoteForConversion.id);
      const total = calculatePricing(selectedQuoteForConversion, viewMode);
      alert(`Quote ${selectedQuoteForConversion.id} converted to official order!\n\n` + `Order ID: ORD-${selectedQuoteForConversion.id}\n` + `Total: ${selectedQuoteForConversion.currency} $${total.toFixed(2)}\n` + `View: ${viewMode === 'dealer' ? 'Dealer Pricing' : 'Customer Pricing'}\n` + `Status: Processing\n\n` + `Redirecting to operations workflow...`);
      setShowConvertModal(false);
      setSelectedQuoteForConversion(null);
    }
  };
  return <div className="w-full min-h-screen" data-id="element-441">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-b-2 border-blue-600/30 shadow-xl" data-id="element-442">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-443">
          <div className="flex items-center justify-between" data-id="element-444">
            <div data-id="element-445">
              <h1 className="text-3xl font-bold text-white mb-2" data-id="element-446">
                Cotizaciones Guardadas
              </h1>
              <p className="text-blue-200" data-id="element-447">
                Gestiona todas tus cotizaciones en un solo lugar
              </p>
            </div>
            <button onClick={onNewQuote} className="flex items-center gap-2 px-6 py-3 text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-all font-bold shadow-lg hover:shadow-xl hover:scale-105" data-id="element-448">
              <PlusCircleIcon className="w-5 h-5" data-id="element-449" />
              Nueva Cotización
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-450">
        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-id="element-451">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-452">
            <div className="flex-1 relative" data-id="element-453">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-454" />
              <input type="text" placeholder="Buscar cotizaciones..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-455" />
            </div>
            <div className="flex gap-2" data-id="element-456">
              <button onClick={() => setFilterStatus('all')} className={`px-4 py-3 rounded-lg font-semibold transition-all ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-457">
                Todas
              </button>
              <button onClick={() => setFilterStatus('draft')} className={`px-4 py-3 rounded-lg font-semibold transition-all ${filterStatus === 'draft' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-458">
                Borradores
              </button>
              <button onClick={() => setFilterStatus('finalized')} className={`px-4 py-3 rounded-lg font-semibold transition-all ${filterStatus === 'finalized' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-459">
                Finalizadas
              </button>
            </div>
          </div>
        </div>

        {/* Quotes Grid */}
        {filteredQuotes.length === 0 ? <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-460">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-461">
              <FileTextIcon className="w-10 h-10 text-gray-400" data-id="element-462" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-463">
              No hay cotizaciones
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-464">
              {searchTerm ? 'No se encontraron cotizaciones con ese criterio de búsqueda' : 'Comienza creando tu primera cotización'}
            </p>
            {!searchTerm && <button onClick={onNewQuote} className="inline-flex items-center gap-2 px-6 py-3 text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all font-bold" data-id="element-465">
                <PlusCircleIcon className="w-5 h-5" data-id="element-466" />
                Crear Primera Cotización
              </button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-467">
            {filteredQuotes.map(quote => {
          const viewMode = getViewMode(quote.id);
          const total = calculatePricing(quote, viewMode);
          return <div key={quote.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all" data-id="element-468">
                  <div className="p-6" data-id="element-469">
                    <div className="flex items-start justify-between mb-4" data-id="element-470">
                      <div className="flex-1" data-id="element-471">
                        <div className="flex items-center gap-2 mb-2" data-id="element-472">
                          <h3 className="text-lg font-semibold text-gray-900" data-id="element-473">
                            {quote.name || 'Sin nombre'}
                          </h3>
                          {quote.status === 'draft' && <span className="px-2.5 py-1 text-xs font-bold text-amber-700 bg-amber-100 rounded-full border border-amber-300" data-id="element-474">
                              DRAFT
                            </span>}
                          {quote.status === 'finalized' && <span className="px-2.5 py-1 text-xs font-bold text-green-700 bg-green-100 rounded-full border border-green-300" data-id="element-475">
                              ACTIVE
                            </span>}
                          {quote.status === 'expired' && <span className="px-2.5 py-1 text-xs font-bold text-gray-700 bg-gray-100 rounded-full border border-gray-300" data-id="element-476">
                              EXPIRED
                            </span>}
                          {quote.status === 'deleted' && <span className="px-2.5 py-1 text-xs font-bold text-red-700 bg-red-100 rounded-full border border-red-300" data-id="element-477">
                              DELETED
                            </span>}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-478">
                          <CalendarIcon className="w-4 h-4" data-id="element-479" />
                          {new Date(quote.createdAt).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4" data-id="element-480">
                      <div className="flex items-center justify-between text-sm" data-id="element-481">
                        <span className="text-gray-600" data-id="element-482">Piezas:</span>
                        <span className="font-semibold text-gray-900" data-id="element-483">
                          {quote.parts.length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm" data-id="element-484">
                        <span className="text-gray-600" data-id="element-485">Pivots:</span>
                        <span className="font-semibold text-gray-900" data-id="element-486">
                          {quote.pivots.length}
                        </span>
                      </div>

                      {/* View Mode Toggle for dealer-customer quotes */}
                      {quote.quoteType === 'dealer-customer' && <div className="pt-2 border-t border-gray-200" data-id="element-487">
                          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-id="element-488">
                            <button onClick={() => toggleViewMode(quote.id)} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'dealer' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`} data-id="element-489">
                              <BuildingIcon className="w-3 h-3" data-id="element-490" />
                              Dealer
                            </button>
                            <button onClick={() => toggleViewMode(quote.id)} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'customer' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`} data-id="element-491">
                              <UserIcon className="w-3 h-3" data-id="element-492" />
                              Customer
                            </button>
                          </div>
                        </div>}

                      <div className="pt-2 border-t border-gray-200" data-id="element-493">
                        <div className="flex items-center justify-between" data-id="element-494">
                          <span className="text-sm text-gray-600 flex items-center gap-1" data-id="element-495">
                            <DollarSignIcon className="w-4 h-4" data-id="element-496" />
                            Total:
                          </span>
                          <span className="text-lg font-bold text-gray-900" data-id="element-497">
                            {quote.currency} ${total.toFixed(2)}
                          </span>
                        </div>
                        {quote.quoteType === 'dealer-customer' && <p className="text-xs text-gray-500 text-right mt-1" data-id="element-498">
                            {viewMode === 'dealer' ? 'Dealer pricing' : 'Customer pricing'}
                          </p>}
                      </div>
                    </div>

                    {/* Convert to Order Button */}
                    {quote.status === 'finalized' && <button onClick={() => handleConvertToOrder(quote)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-semibold shadow-md hover:shadow-lg" data-id="element-499">
                        <ShoppingCartIcon className="w-4 h-4" data-id="element-500" />
                        Convert to Order
                      </button>}

                    <div className="flex gap-2" data-id="element-501">
                      <button onClick={() => onViewQuote(quote.id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all font-semibold" data-id="element-502">
                        <EyeIcon className="w-4 h-4" data-id="element-503" />
                        Ver
                      </button>
                      <button onClick={() => onEditQuote(quote.id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all font-semibold" data-id="element-504">
                        <EditIcon className="w-4 h-4" data-id="element-505" />
                        Editar
                      </button>
                      <button onClick={() => onDeleteQuote(quote.id)} className="px-4 py-2.5 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-all" data-id="element-506">
                        <TrashIcon className="w-4 h-4" data-id="element-507" />
                      </button>
                    </div>
                  </div>
                </div>;
        })}
          </div>}

        {/* Summary */}
        {filteredQuotes.length > 0 && <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6" data-id="element-508">
            <div className="flex items-center justify-between" data-id="element-509">
              <div data-id="element-510">
                <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide mb-1" data-id="element-511">
                  Resumen
                </h3>
                <p className="text-gray-700" data-id="element-512">
                  <span className="font-semibold text-gray-900" data-id="element-513">
                    {filteredQuotes.length}
                  </span>{' '}
                  cotizaciones encontradas
                </p>
              </div>
            </div>
          </div>}
      </div>

      {/* Convert to Order Confirmation Modal */}
      {showConvertModal && selectedQuoteForConversion && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-514">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" data-id="element-515">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 relative rounded-t-2xl" data-id="element-516">
              <button onClick={() => {
            setShowConvertModal(false);
            setSelectedQuoteForConversion(null);
          }} className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors" data-id="element-517">
                <XIcon className="w-5 h-5" data-id="element-518" />
              </button>
              <div className="flex items-center gap-3" data-id="element-519">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-520">
                  <PackageIcon className="w-6 h-6 text-white" data-id="element-521" />
                </div>
                <div data-id="element-522">
                  <h2 className="text-2xl font-bold text-white" data-id="element-523">
                    Convert to Order
                  </h2>
                  <p className="text-green-100 text-sm" data-id="element-524">
                    Start operations workflow
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4" data-id="element-525">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-526">
                <p className="text-sm text-gray-700 mb-3" data-id="element-527">
                  <span className="font-semibold" data-id="element-528">Quote:</span>{' '}
                  {selectedQuoteForConversion.name || selectedQuoteForConversion.id}
                </p>
                <p className="text-sm text-gray-700 mb-3" data-id="element-529">
                  <span className="font-semibold" data-id="element-530">Total Amount:</span>{' '}
                  {selectedQuoteForConversion.currency} $
                  {calculatePricing(selectedQuoteForConversion, getViewMode(selectedQuoteForConversion.id)).toFixed(2)}
                </p>
                <p className="text-sm text-gray-700" data-id="element-531">
                  <span className="font-semibold" data-id="element-532">View:</span>{' '}
                  {getViewMode(selectedQuoteForConversion.id) === 'dealer' ? 'Dealer Pricing' : 'Customer Pricing'}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-533">
                <p className="text-sm text-amber-800" data-id="element-534">
                  <span className="font-semibold" data-id="element-535">⚠️ Important:</span>{' '}
                  Converting this quote to an official order will:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-700 ml-4" data-id="element-536">
                  <li data-id="element-537">• Create a new order in the system</li>
                  <li data-id="element-538">• Start the operations workflow</li>
                  <li data-id="element-539">• Lock the pricing and specifications</li>
                  <li data-id="element-540">• Notify relevant departments</li>
                </ul>
              </div>

              <div className="flex gap-3" data-id="element-541">
                <button onClick={handleConfirmConvert} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all font-semibold shadow-lg" data-id="element-542">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-543" />
                  Confirm & Convert
                </button>
                <button onClick={() => {
              setShowConvertModal(false);
              setSelectedQuoteForConversion(null);
            }} className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold" data-id="element-544">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}